import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CANAIS_CONTATO, ROTULO_CANAL, type CanalContato } from '@reducee/contracts';
import { useEffect, useState, type FormEvent } from 'react';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import { CampoSelecao, CampoTexto } from './Campo.tsx';

type Passo = { ordem: number; diaOffset: number; canal: CanalContato; titulo: string };

export type Modelo = {
  id: string;
  nome: string;
  descricao: string | null;
  ativo: boolean;
  padrao: boolean;
  passos: Passo[];
};

/**
 * Resumo do roteiro em uma linha, como o aviso do protótipo:
 * "D0 Ligação → D0 WhatsApp → D2 E-mail → …".
 */
export function ResumoDoRoteiro({ modelo }: { modelo: Modelo }): React.JSX.Element {
  return (
    <p className="rounded-xl bg-marinho-suave p-3 text-sm leading-relaxed">
      <span className="font-semibold">Modelo {modelo.padrao ? 'padrão' : modelo.nome}:</span>{' '}
      {modelo.passos.map((passo) => `D${passo.diaOffset} ${ROTULO_CANAL[passo.canal]}`).join(' → ')}
      . Ao registrar resposta, você escolhe entre pausar ou manter a cadência ativa. Se terminar sem
      resposta, o lead vai para Reciclagem e recebe tarefa de reativação.
    </p>
  );
}

/** Carrega o modelo padrão de cadência, compartilhado entre Sales e Configurações. */
export function useModeloDeCadencia(): Modelo | null {
  const consulta = useQuery({
    queryKey: ['cadencia-modelos'],
    queryFn: () => chamarApi<Modelo[]>('/cadencia-modelos'),
  });
  return consulta.data?.[0] ?? null;
}

/**
 * Edição do roteiro de cadência, sem cabeçalho próprio.
 *
 * O roteiro é salvo por inteiro, na ordem da tela. As cadências já em andamento
 * não mudam: elas guardam as próprias tentativas agendadas, e reescrevê-las sob
 * os pés de quem está atendendo confundiria o acompanhamento.
 */
export function RoteiroDeCadencia(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [passos, setPassos] = useState<Passo[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  const modelo = useModeloDeCadencia();

  useEffect(() => {
    if (modelo) setPassos(modelo.passos);
  }, [modelo]);

  const salvar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chamarApi<Modelo>(`/cadencia-modelos/${modelo?.id}`, { metodo: 'PATCH', corpo }),
    onSuccess: async () => {
      setErro(null);
      setSalvo(true);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cadencia-modelos'] });
    },
    onError: (causa: unknown) => {
      setSalvo(false);
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar o roteiro.');
    },
  });

  function alterarPasso(indice: number, mudanca: Partial<Passo>): void {
    setSalvo(false);
    setPassos((atuais) =>
      atuais.map((passo, posicao) => (posicao === indice ? { ...passo, ...mudanca } : passo)),
    );
  }

  function aoEnviar(evento: FormEvent): void {
    evento.preventDefault();
    setErro(null);
    salvar.mutate({
      passos: passos.map((passo) => ({
        diaOffset: passo.diaOffset,
        canal: passo.canal,
        titulo: passo.titulo,
      })),
    });
  }

  if (!modelo) return <p className="text-texto-suave">Carregando…</p>;

  return (
    <form onSubmit={aoEnviar}>
      {erro ? (
        <p role="alert" className="mb-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}
      {salvo ? (
        <p
          data-teste="roteiro-salvo"
          className="mb-4 rounded-lg bg-sucesso/10 px-3 py-2 text-sm text-sucesso"
        >
          Roteiro salvo.
        </p>
      ) : null}

      <ul className="space-y-3">
        {passos.map((passo, indice) => (
          <li
            key={indice}
            data-teste="passo-modelo"
            className="grid gap-3 rounded-lg bg-marinho-suave p-3 md:grid-cols-[4rem_10rem_1fr_auto]"
          >
            <CampoTexto
              rotulo="Dia"
              name={`dia-${indice}`}
              type="number"
              min={0}
              value={String(passo.diaOffset)}
              onChange={(evento) => alterarPasso(indice, { diaOffset: Number(evento.target.value) })}
            />
            <CampoSelecao
              rotulo="Canal"
              name={`canal-${indice}`}
              value={passo.canal}
              onChange={(evento) =>
                alterarPasso(indice, { canal: evento.target.value as CanalContato })
              }
            >
              {CANAIS_CONTATO.map((canal) => (
                <option key={canal} value={canal}>
                  {ROTULO_CANAL[canal]}
                </option>
              ))}
            </CampoSelecao>
            <CampoTexto
              rotulo="Título"
              name={`titulo-${indice}`}
              value={passo.titulo}
              onChange={(evento) => alterarPasso(indice, { titulo: evento.target.value })}
            />
            <button
              type="button"
              onClick={() => {
                setSalvo(false);
                setPassos((atuais) => atuais.filter((_, posicao) => posicao !== indice));
              }}
              className="self-end rounded-[10px] border border-linha bg-superficie px-3.25 py-2 text-sm transition hover:bg-fundo"
            >
              Remover
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => {
            setSalvo(false);
            setPassos((atuais) => [
              ...atuais,
              {
                ordem: atuais.length + 1,
                diaOffset: (atuais.at(-1)?.diaOffset ?? 0) + 1,
                canal: 'LIGACAO',
                titulo: 'Nova tentativa',
              },
            ]);
          }}
          className="rounded-[10px] border border-linha px-4 py-2 text-sm font-bold transition hover:bg-marinho-suave"
        >
          Adicionar passo
        </button>
        <button
          type="submit"
          disabled={salvar.isPending || passos.length === 0}
          className="rounded-[10px] bg-marinho px-4 py-2 text-sm font-bold text-white transition hover:bg-marinho-claro disabled:opacity-60"
        >
          Salvar roteiro
        </button>
      </div>
    </form>
  );
}
